<template>
  <model-form
    ref="modelForm"
    :model="bookingFeeType"
    modal-type="modal"
  >
    <div class="column">
      <q-tabs
        v-model="tab"
        align="justify"
        dense
      >
        <q-tab
          name="details"
          label="Details"
        />
        <q-tab
          name="restrictions"
          label="Restrictions"
        />
      </q-tabs>

      <div class="row q-col-gutter-sm items-center q-mt-sm">
        <model-form-input
          field="name"
          class="col-6"
          label="Name"
          autofocus
          :rules="[val => !!val || 'Field is required']"
        />
        <model-form-select
          field="type"
          class="col-6"
          label="Type"
          :options="$mh.asOptions(TYPES.ALL)"
          map-options
          emit-value
          :rules="[val => !!val || 'Field is required']"
        >
          <template #option="scope">
            <q-item
              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
            >
              <q-item-section>
                <div class="row items-center justify-between q-gutter-x-sm">
                  <span>{{ scope.opt.label }}</span>
                  <q-icon
                    name="info"
                    size="sm"
                  >
                    <q-tooltip>
                      {{ TYPES_TOOLTIPS[scope.opt.value] }}
                    </q-tooltip>
                  </q-icon>
                </div>
              </q-item-section>
            </q-item>
          </template>
          <q-tooltip>
            Charge this booking fee per order or per ticket. Only one booking fee will be charged per order and ticket.
          </q-tooltip>
        </model-form-select>
      </div>
    </div>

    <q-tab-panels v-model="tab">
      <q-tab-panel
        name="details"
        class="q-pa-none"
      >
        <div class="column">
          <div class="row q-col-gutter-sm items-center">
            <model-form-select
              field="channels"
              class="col-8"
              label="Channels"
              :options="$mh.asOptions(CHANNELS.ALL)"
              map-options
              emit-value
              multiple
              use-chips
              clearable
              :decode="channels => $mh.objectToArray(channels)"
              :encode="enabledChannels => $mh.arrayToObject(enabledChannels || [], { allKeys: CHANNELS.ALL })"
              :rules="[
                val => !!val || 'Field is required',
                val => !bookingFeeType.enabled || val.length > 0 || 'Must select at least one channel'
              ]"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <div class="row items-center justify-between q-gutter-x-sm">
                      <span>{{ scope.opt.label }}</span>
                      <q-icon
                        name="info"
                        size="sm"
                      >
                        <q-tooltip>
                          {{ CHANNELS_TOOLTIPS[scope.opt.value] }}
                        </q-tooltip>
                      </q-icon>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
              <q-tooltip>
                This Booking Fee will be charged on orders or tickets purchased through the selected channels.
              </q-tooltip>
            </model-form-select>
            <model-form-toggle
              field="enabled"
              class="col-4 q-pb-md"
              label="Enable"
              stack-label
            />
          </div>
          <div class="row q-col-gutter-sm items-center">
            <model-form-select
              field="taxIds"
              class="col-8"
              :label="$t('pricing.tax')"
              :options="taxes.data"
              option-label="name"
              option-value="id"
              map-options
              emit-value
              multiple
              use-chips
              clearable
              :rules="[
                val => !!val || 'Field is required',
                val => !bookingFeeType.taxable || val.length > 0 || `Must disable or select at least one ${$t('pricing.tax')}`
              ]"
            />
            <model-form-toggle
              field="taxable"
              class="col-4 q-pb-md"
              :label="$t('pricing.taxable')"
            >
              <q-tooltip>
                Charge {{ $t('pricing.tax') }} with this booking fee.
              </q-tooltip>
            </model-form-toggle>
          </div>
          <div class="row q-col-gutter-sm items-center">
            <model-form-input
              field="amount"
              class="col-8"
              :label="currentSite.ticketAmountLabel"
              :prefix="currentCircuit.localeCurrencySymbol"
              type="number"
              min="0.01"
              step="0.25"
              :rules="[
                val => !!val || 'Field is required',
                val => val > 0 || 'Must be greater than 0'
              ]"
            >
              <q-tooltip>
                Amount charged when applying this Booking Fee.
              </q-tooltip>
            </model-form-input>
            <model-form-toggle
              v-if="bookingFeeType.type === TYPES.TICKET"
              field="includeInPrice"
              class="col-4 q-pb-md"
              label="Include In Price"
            >
              <q-tooltip>
                Include the amount of this Booking Fee in the ticket price of applicable tickets.
              </q-tooltip>
            </model-form-toggle>
            <model-form-toggle
              field="refundable"
              class="q-pb-md"
              label="Refundable"
            >
              <q-tooltip>
                Refund this Booking Fee online when customers refund their tickets.
              </q-tooltip>
            </model-form-toggle>
            <model-form-toggle
              v-if="bookingFeeType.type === TYPES.TICKET"
              field="refundByDefault"
              class="q-pb-md"
              label="Refund By Default"
            >
              <q-tooltip>
                Refund this Booking Fee automatically when refunding associated tickets.
              </q-tooltip>
            </model-form-toggle>
          </div>
        </div>
      </q-tab-panel>

      <q-tab-panel
        name="restrictions"
        class="q-pa-none"
        style="overflow-y: hidden;"
      >
        <div class="column">
          <model-form-select
            field="conditions"
            label="Conditions"
            :options="$mh.asOptions(CONDITIONS[bookingFeeType.type])"
            map-options
            emit-value
            multiple
            use-chips
            clearable
            :decode="conditions => $mh.objectToArray(conditions)"
            :encode="enabledConditions => $mh.arrayToObject(enabledConditions || [], { allKeys: CONDITIONS.ALL })"
          >
            <template #option="scope">
              <q-item
                v-bind="scope.itemProps"
                v-on="scope.itemEvents"
              >
                <q-item-section>
                  <div class="row items-center justify-between q-gutter-x-sm">
                    <span>{{ scope.opt.label }}</span>
                    <q-icon
                      name="info"
                      size="sm"
                    >
                      <q-tooltip>
                        {{ CONDITIONS_TOOLTIPS[scope.opt.value] }}
                      </q-tooltip>
                    </q-icon>
                  </div>
                </q-item-section>
              </q-item>
            </template>
            <q-tooltip>
              Set conditions to be met for this booking fee to be charged with an order or ticket.
              The first booking fee meeting all conditions will be charged.
              Control the priority of booking fees on the booking fee table.
            </q-tooltip>
          </model-form-select>
          <div class="row q-col-gutter-sm items-center">
            <price-condition-form
              v-if="bookingFeeType.conditions[CONDITIONS.PRICE]"
              :conditional="bookingFeeType"
              :exclude-price-field="CONDITIONS_REQUIREMENTS.EXCLUDE_PRICE"
            />
            <order-item-types-condition-form
              v-if="bookingFeeType.conditions[CONDITIONS.ORDER_ITEM_TYPES]"
              :conditional="bookingFeeType"
            />
            <membership-types-condition-form
              v-if="bookingFeeType.conditions[CONDITIONS.MEMBERSHIP_TYPES]"
              :conditional="bookingFeeType"
            />
            <showing-badges-condition-form
              v-if="bookingFeeType.conditions[CONDITIONS.SHOWING_BADGES]"
              :conditional="bookingFeeType"
            />
            <ticket-types-condition-form
              v-if="bookingFeeType.conditions[CONDITIONS.TICKET_TYPES]"
              :conditional="bookingFeeType"
            />
          </div>
        </div>
      </q-tab-panel>
    </q-tab-panels>
  </model-form>
</template>

<script>
import { CHANNELS, CONDITIONS, CONDITIONS_REQUIREMENTS, TYPES } from 'src/models/booking-fee-type/BookingFeeType'
import { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import MembershipTypesConditionForm from 'src/modules/admin/components/condition-requirement-forms/MembershipTypesConditionForm'
import OrderItemTypesConditionForm from 'src/modules/admin/components/condition-requirement-forms/OrderItemTypesConditionForm'
import PriceConditionForm from 'src/modules/admin/components/condition-requirement-forms/PriceConditionForm'
import ShowingBadgesConditionForm from 'src/modules/admin/components/condition-requirement-forms/ShowingBadgesConditionForm'
import TicketTypesConditionForm from 'src/modules/admin/components/condition-requirement-forms/TicketTypesConditionForm'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'

const CHANNELS_TOOLTIPS = {
  [CHANNELS.EXTERNAL]: 'Booking Fee will be charged for orders or tickets purchased through the external ticketers.',
  [CHANNELS.KIOSK]: 'Booking Fee will be charged for orders or tickets purchased through the kiosk.',
  [CHANNELS.PHONE]: 'Booking Fee will be charged for orders or tickets purchased by the phone.',
  [CHANNELS.REGISTER]: 'Booking Fee will be charged for orders or tickets purchased in person.',
  [CHANNELS.WEBSITE]: 'Booking Fee will be charged for orders or tickets purchased through the website and app.'
}
const CONDITIONS_TOOLTIPS = {
  [CONDITIONS.PRICE]: 'Booking Fee will be charged on orders or tickets meeting the given price range.',
  [CONDITIONS.ORDER_ITEM_TYPES]: 'Booking Fee will be charged on orders including ANY order items matching the given type.',
  [CONDITIONS.MEMBERSHIP_TYPES]: 'Booking Fee will be charged on orders or tickets purchased for members matching the given membership type.',
  [CONDITIONS.SHOWING_BADGES]: 'Booking Fee will be charged on orders or tickets including ALL of the given showing badges.',
  [CONDITIONS.TICKET_TYPES]: 'Booking Fee will be charged for tickets matching the given ticket type.'
}
const TYPES_TOOLTIPS = {
  [TYPES.ORDER]: 'Booking Fee will be charged on applicable orders. Maximum of one order booking fee will be charged per order.',
  [TYPES.TICKET]: 'Booking Fee will be charged for applicable tickets. Maximum of one ticket booking fee will be charged per ticket.'
}

export default {
  name: 'BookingFeeTypeForm',
  components: {
    ModelForm,
    PriceConditionForm,
    MembershipTypesConditionForm,
    OrderItemTypesConditionForm,
    ShowingBadgesConditionForm,
    TicketTypesConditionForm
  },
  configuration: ['currentCircuit', 'currentSite', 'taxes'],
  models: {
    bookingFeeType: {
      membershipTypes: {
        fields: ['id', 'name']
      },
      showingBadges: {
        fields: ['id', 'name']
      },
      taxes: {
        fields: ['id', 'name']
      },
      ticketTypes: {
        fields: ['id', 'name']
      }
    }
  },
  data () {
    return {
      tab: 'details'
    }
  },
  watch: {
    'bookingFeeType.type' () {
      const excludedConditions = _.without(CONDITIONS.ALL, ...CONDITIONS[this.bookingFeeType.type])

      _.each(excludedConditions, condition => { this.bookingFeeType.conditions[condition] = false })
    }
  },
  created () {
    _.extend(this, {
      CHANNELS,
      CHANNELS_TOOLTIPS,
      CONDITIONS,
      CONDITIONS_REQUIREMENTS,
      CONDITIONS_TOOLTIPS,
      ORDER_ITEM_TYPES,
      TYPES,
      TYPES_TOOLTIPS
    })
  }
}
</script>
